import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import { fetchMerchantCouponDetail } from "../api/merchantCouponEditor.api";
import type { MerchantCouponUIState } from "../data/merchantCouponEditor.data";

interface UseGetMerchantCouponDetailReturn {
  coupon: MerchantCouponUIState | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useGetMerchantCouponDetail = (couponId?: string): UseGetMerchantCouponDetailReturn => {
  const [coupon, setCoupon] = useState<MerchantCouponUIState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchDetail = useCallback(async () => {
    if (!couponId) {
      setCoupon(null);
      setError(null);
      return;
    }

    try {
      setError(null);
      const detail = await runLatestRequest((signal) =>
        fetchMerchantCouponDetail(couponId, signal),
      );

      if (!detail) return;
      setCoupon(detail);
    } catch (requestError: unknown) {
      setCoupon(null);
      setError(getApiErrorMessage(requestError, "Failed to load coupon detail"));
    }
  }, [couponId, runLatestRequest]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  return {
    coupon,
    loading,
    error,
    refetch: fetchDetail,
  };
};
